import axios from 'axios';

/**
 * API client configuration
 * 
 * This client is configured for the BYOK admin API.
 * Uses token-based authentication for admin users.
 */

// Base URL for the API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Token storage key
const TOKEN_KEY = 'adminToken';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Get stored auth token
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Set auth token
 */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Remove auth token
 */
export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Check if token exists
 */
export function hasToken(): boolean {
  return !!getToken();
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    
    if (import.meta.env.DEV) {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, config.params);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // Unauthorized - token invalid or expired
          console.error('[API] Unauthorized - redirecting to login');
          removeToken();
          // Dispatch event for auth context to handle
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          break;
        case 403:
          console.error('[API] Forbidden - insufficient permissions');
          break;
        case 404:
          console.error('[API] Resource not found');
          break;
        case 500:
          console.error('[API] Server error');
          break;
        default:
          console.error(`[API] Error: ${error.response.status}`);
      }
    } else if (error.request) {
      console.error('[API] No response received:', error.request);
    } else {
      console.error('[API] Request error:', error.message);
    }
    return Promise.reject(error);
  }
);

export default apiClient;
